import React, { useEffect, useMemo, useState } from "react";
import { Button, Modal, Spinner } from "reactstrap";
import moment from "moment";

import api from "../../../services/api";
import { rotuloDocumento } from "../../../utils/modeloDocumento";
import EtiquetaTab from "./EtiquetaTab"; // PO1-3378

const fmtCurrency = (value) =>
  value == null
    ? "-"
    : new Intl.NumberFormat("pt-BR", {
        style: "currency",
        currency: "BRL",
      }).format(Number(value || 0));

const fmtNumber = (value, suffix = "") =>
  value == null
    ? "-"
    : `${new Intl.NumberFormat("pt-BR", { maximumFractionDigits: 3 }).format(Number(value))}${suffix}`;

// Data sem hora (emissão, 00:00Z) em UTC para não cair no dia anterior; com hora, no fuso local.
const fmtDate = (value, withTime = false) =>
  value ? (withTime ? moment(value).format("DD/MM/YYYY HH:mm") : moment.utc(value).format("DD/MM/YYYY")) : "-";

const location = (city, state) => [city, state].filter(Boolean).join("/") || "-";

const statusMeta = (summary) => {
  if (summary && summary.divergencia) return { label: "Com divergência", tone: "danger" };
  const statuses = {
    0: { label: "Pendente", tone: "warning" },
    2: { label: "Não calculado", tone: "neutral" },
    3: { label: "Calculado", tone: "success" },
    4: { label: "Recalcular", tone: "info" },
    5: { label: "Frota própria (sem cálculo)", tone: "neutral" },
  };
  return statuses[Number(summary && summary.statusCalculo)] || { label: "Sem status", tone: "neutral" };
};

const redespachoNome = (summary) =>
  summary.redespachoFantasia || summary.redespachoRazaoSocial || summary.redespachoCnpj || "-";

const somar = (ctes, campo) => ctes.reduce((sum, cte) => sum + Number(cte[campo] || 0), 0);

const TRECHO_LABEL = { redespacho: "Redespacho", complementar: "Complementar" };

const Field = ({ label, value, wide = false, mono = false }) => (
  <div className={`nfe-detail-field${wide ? " nfe-detail-field--wide" : ""}`}>
    <span>{label}</span>
    <strong className={mono ? "nfe-detail-mono" : ""}>{value == null || value === "" ? "-" : value}</strong>
  </div>
);

const Section = ({ eyebrow, title, children, className = "" }) => (
  <section className={`nfe-detail-section ${className}`}>
    <div className="nfe-detail-section__header">
      {eyebrow && <span>{eyebrow}</span>}
      <h4>{title}</h4>
    </div>
    {children}
  </section>
);

const FinancialTable = ({ composicao }) => {
  const rows = [
    ...(composicao.custos || []).map((item) => ({ ...item, group: "Frete", amount: item.total })),
    ...(composicao.taxas || []).map((item) => ({ ...item, group: "Taxa", amount: item.total })),
    ...(composicao.tributos || []).map((item) => ({ ...item, group: "Tributo", amount: item.valor })),
  ];
  const total = rows.reduce((sum, item) => sum + Number(item.amount || 0), 0);

  if (!rows.length) {
    return <div className="nfe-detail-empty">Não há memória de cálculo persistida para esta NFe.</div>;
  }

  return (
    <div className="nfe-detail-table-wrap">
      <table className="nfe-detail-table nfe-detail-table--composition hub-thead-accent">
        <thead>
          <tr>
            <th>Grupo</th>
            <th>Item calculado</th>
            <th>Fórmula</th>
            <th className="is-number">Calculado</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((item) => (
            <tr key={`${item.group}-${item.id}`}>
              <td><span className="nfe-detail-group-label">{item.group}</span></td>
              <td>
                <strong>{item.nome || "-"}</strong>
                {(item.unidadeRegra || item.codigo) && <small>{item.codigo || item.unidadeRegra}</small>}
              </td>
              <td className="nfe-detail-formula">
                <strong>{item.formula || "-"}</strong>
                {(item.faixaDeAte || item.cepDeAte || item.custoMinMax) && (
                  <small>{[item.faixaDeAte, item.cepDeAte, item.custoMinMax].filter(Boolean).join(" | ")}</small>
                )}
              </td>
              <td className="is-number">{fmtCurrency(item.amount)}</td>
            </tr>
          ))}
          <tr className="nfe-detail-total-row">
            <td colSpan="3">Total da composição</td>
            <td className="is-number">{fmtCurrency(total)}</td>
          </tr>
        </tbody>
      </table>
    </div>
  );
};

const OverviewTab = ({ summary }) => (
  <div className="nfe-detail-overview-grid">
    <Section eyebrow="Documento" title="Identificação e contexto">
      <div className="nfe-detail-fields nfe-detail-fields--three">
        <Field label="Emissão" value={fmtDate(summary.dataEmissao)} />
        <Field label="Pedido" value={summary.pedido} />
        <Field label="Modal" value={summary.modal} />
        <Field label="Negócio" value={summary.negocio} />
        <Field label="Tipo de serviço" value={summary.tipoServico || summary.servico} />
        <Field label="Natureza da operação" value={summary.naturezaOperacao} />
      </div>
    </Section>

    <Section eyebrow="Operação" title="Rota e participantes">
      <div className="nfe-detail-route">
        <span>Rota principal</span>
        <strong>{location(summary.remetenteCidade, summary.remetenteUf)} → {location(summary.destinatarioCidade, summary.destinatarioUf)}</strong>
      </div>
      <div className={`nfe-detail-party-grid${summary.redespachoCnpj ? " nfe-detail-party-grid--four" : ""}`}>
        <div>
          <span>Remetente</span>
          <strong>{summary.remetenteFantasia || "-"}</strong>
          <small>{summary.remetenteCnpjCpf || "-"}</small>
        </div>
        <div>
          <span>Destinatário</span>
          <strong>{summary.destinatarioRazaoSocial || "-"}</strong>
          <small>{summary.destinatarioCnpjCpf || "-"}</small>
        </div>
        <div>
          <span>Transportadora</span>
          <strong>{summary.transportadoraFantasia || "-"}</strong>
          <small>{summary.transportadoraCnpjCpf || "-"}</small>
        </div>
        {summary.redespachoCnpj && (
          <div className="nfe-detail-party--redespacho">
            <span>Redespacho</span>
            <strong>{redespachoNome(summary)}</strong>
            <small>
              {[summary.redespachoCnpj, summary.redespachoModal].filter(Boolean).join(" · ")}
              {!summary.redespachoTransportadoraId && " · CNPJ sem cadastro no Hub"}
            </small>
          </div>
        )}
      </div>
    </Section>

    <Section eyebrow="Carga" title="Volumes e valores" className="nfe-detail-section--full">
      <div className="nfe-detail-fields nfe-detail-fields--six">
        <Field label="Itens" value={fmtNumber(summary.quantidadeItens)} />
        <Field label="Volumes" value={fmtNumber(summary.volumes)} />
        <Field label="Peso bruto" value={fmtNumber(summary.pesoBruto, " kg")} />
        <Field label="Peso cubado" value={fmtNumber(summary.pesoCubado, " kg")} />
        <Field label="Mercadoria" value={fmtCurrency(summary.valorMercadoria)} />
        <Field label="Valor da NFe" value={fmtCurrency(summary.valorNota)} />
      </div>
    </Section>
  </div>
);

const CalculationTab = ({ summary, composicao, composicaoRedespacho, ctes }) => {
  const comRedespacho = Boolean(summary.redespachoCnpj);
  const ctesRedespacho = ctes.filter((cte) => cte.trecho === "redespacho");

  return (
    <div className="nfe-detail-calculation">
      <Section
        eyebrow={comRedespacho ? `Trecho principal · ${summary.transportadoraFantasia || "-"}` : "Regra selecionada"}
        title="Tabela e contexto do cálculo"
      >
        <div className="nfe-detail-fields nfe-detail-fields--three">
          <Field label="Tabela de preço" value={summary.tabelaPreco} wide />
          <Field label="Transportadora da tabela" value={summary.tabelaTransportadora} />
          <Field label="Tipo de serviço da tabela" value={summary.tipoServicoTabela} />
          <Field label="Tipo de frete" value={summary.tipoFreteTabela} />
          <Field label="Serviço" value={summary.tabelaServico} />
        </div>
      </Section>

      <div className="nfe-detail-financial-summary">
        <Field label="Frete informado" value={fmtCurrency(summary.valorFreteInformado)} />
        <Field label="Frete simulado" value={fmtCurrency(summary.valorFreteSimulado)} />
        <Field label="Frete combinado" value={fmtCurrency(summary.valorFreteCombinado)} />
        <Field
          label="Diferença"
          value={
            summary.valorFreteInformado == null || summary.valorFreteSimulado == null
              ? "-"
              : fmtCurrency(Number(summary.valorFreteInformado) - Number(summary.valorFreteSimulado))
          }
        />
      </div>

      <Section eyebrow="Memória" title="Composição do frete">
        <FinancialTable composicao={composicao} />
      </Section>

      {comRedespacho && (
        <RedespachoCalculation
          summary={summary}
          composicao={composicaoRedespacho || {}}
          ctesRedespacho={ctesRedespacho}
        />
      )}
    </div>
  );
};

// Trecho da transportadora de redespacho declarada na NF: calculado a partir
// do CT-e dela (cidades do CT-e), com simulado próprio, separado do principal.
const RedespachoCalculation = ({ summary, composicao, ctesRedespacho }) => {
  const cobrado = somar(ctesRedespacho, "valorFreteReceber");
  const simulado = somar(ctesRedespacho, "valorFreteSimulado");
  const semCte = !ctesRedespacho.length;

  return (
    <div className="nfe-detail-calculation nfe-detail-calculation--redespacho">
      <Section eyebrow={`Trecho de redespacho · ${redespachoNome(summary)}`} title="Tabela e contexto do cálculo">
        <div className="nfe-detail-fields nfe-detail-fields--three">
          <Field label="Tabela de preço" value={summary.tabelaPrecoRedespacho} wide />
          <Field label="Transportadora da tabela" value={summary.tabelaTransportadoraRedespacho} />
          <Field label="Tipo de serviço da tabela" value={summary.tipoServicoTabelaRedespacho} />
          <Field label="Tipo de frete" value={summary.tipoFreteTabelaRedespacho} />
          <Field label="Serviço" value={summary.tabelaServicoRedespacho} />
        </div>
      </Section>

      <div className="nfe-detail-financial-summary">
        <Field
          label="Documento do redespacho"
          value={semCte ? "-" : ctesRedespacho.map((cte) => `${cte.numero}/${cte.serie}`).join(", ")}
        />
        <Field label="Frete cobrado" value={semCte ? "-" : fmtCurrency(cobrado)} />
        <Field label="Frete simulado" value={semCte ? "-" : fmtCurrency(simulado)} />
        <Field label="Diferença" value={semCte ? "-" : fmtCurrency(cobrado - simulado)} />
      </div>

      <Section eyebrow="Memória" title="Composição do frete do redespacho">
        {semCte ? (
          <div className="nfe-detail-empty">Nenhum documento da transportadora de redespacho vinculado a esta NFe.</div>
        ) : (
          <FinancialTable composicao={composicao} />
        )}
      </Section>
    </div>
  );
};

const ItemsTab = ({ itens }) => {
  if (!itens.length) {
    return <div className="nfe-detail-empty">Esta NFE não possui itens persistidos.</div>;
  }

  const totalQuantidade = itens.reduce((sum, item) => sum + Number(item.quantidade || 0), 0);
  const totalValor = itens.reduce((sum, item) => sum + Number(item.valor || 0), 0);

  return (
    <Section eyebrow="Produtos" title="Itens da NFE" className="nfe-detail-section--items">
      <div className="nfe-detail-table-wrap">
        <table className="nfe-detail-table nfe-detail-table--items hub-thead-accent">
          <thead>
            <tr>
              <th>Item</th>
              <th>Código / descrição</th>
              <th className="is-number">Quantidade</th>
              <th className="is-number">Valor</th>
              <th className="is-number">Peso bruto</th>
              <th className="is-number">Peso líquido</th>
              <th className="is-number">Peso cubado</th>
              <th className="is-number">Volumes</th>
              <th>CFOP / centro de custo</th>
            </tr>
          </thead>
          <tbody>
            {itens.map((item) => (
              <tr key={item.id}>
                <td><strong>{item.numeroItem || "-"}</strong></td>
                <td className="nfe-detail-item-product">
                  <strong>{item.codigo || "Sem código"}</strong>
                  {item.descricao && <small>{item.descricao}</small>}
                </td>
                <td className="is-number">
                  {fmtNumber(item.quantidade)}
                  {item.unidade && <small>{item.unidade}</small>}
                </td>
                <td className="is-number">{fmtCurrency(item.valor)}</td>
                <td className="is-number">{fmtNumber(item.pesoBruto, " kg")}</td>
                <td className="is-number">{fmtNumber(item.pesoLiquido, " kg")}</td>
                <td className="is-number">{fmtNumber(item.pesoCubado, " kg")}</td>
                <td className="is-number">{fmtNumber(item.volumes)}</td>
                <td>
                  <strong>{item.cfop || "-"}</strong>
                  {item.centroCusto && <small>CC {item.centroCusto}</small>}
                </td>
              </tr>
            ))}
            <tr className="nfe-detail-total-row">
              <td colSpan="2">Total dos itens</td>
              <td className="is-number">{fmtNumber(totalQuantidade)}</td>
              <td className="is-number">{fmtCurrency(totalValor)}</td>
              <td colSpan="5" />
            </tr>
          </tbody>
        </table>
      </div>
    </Section>
  );
};

const LinksTab = ({ summary, ctes }) => (
  <div className="nfe-detail-links">
    <Section eyebrow="Documentos" title="Documentos vinculados">
      {!ctes.length ? (
        <div className="nfe-detail-empty">Esta NFe ainda não possui documento de transporte vinculado.</div>
      ) : (
        <div className="nfe-detail-table-wrap">
          <table className="nfe-detail-table hub-thead-accent">
            <thead>
              <tr>
                <th>Documento</th>
                <th>Transportadora</th>
                <th>Emissão</th>
                <th className="is-number">Frete a receber</th>
                <th className="is-number">Simulado</th>
                <th>Situação</th>
              </tr>
            </thead>
            <tbody>
              {ctes.map((cte) => (
                <tr key={cte.id}>
                  <td>
                    <strong>{rotuloDocumento(cte.modelo)} {cte.numero} / {cte.serie}</strong>
                    <small className="nfe-detail-mono">{cte.chaveAcesso}</small>
                  </td>
                  <td>
                    <strong>{cte.transportadora || "-"}</strong>
                    {TRECHO_LABEL[cte.trecho] && (
                      <span className="nfe-detail-pill nfe-detail-pill--info">{TRECHO_LABEL[cte.trecho]}</span>
                    )}
                  </td>
                  <td>{fmtDate(cte.dataEmissao)}</td>
                  <td className="is-number">{fmtCurrency(cte.valorFreteReceber)}</td>
                  <td className="is-number">{fmtCurrency(cte.valorFreteSimulado)}</td>
                  <td>
                    <span className={`nfe-detail-pill nfe-detail-pill--${cte.divergencia ? "danger" : "success"}`}>
                      {cte.divergencia ? "Divergente" : "Sem divergência"}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Section>

    <Section eyebrow="Integração" title="Origem do registro">
      <div className="nfe-detail-fields nfe-detail-fields--three">
        <Field label="Integrada em" value={fmtDate(summary.dataIntegracao, true)} />
        <Field label="Situação operacional" value={summary.situacao} />
        <Field label="Status do cálculo" value={statusMeta(summary).label} />
      </div>
    </Section>
  </div>
);

// Aba Ocorrências (PO1-2967, épico PO1-2280): eventos de rastreio da NF
// (NotaFiscalOcorrencia), como no detalhe da Pesquisa Importação do FLOW. Só
// aparece com `mostrarOcorrencias`; a Consulta NFe continua como era.
export const OcorrenciasTab = ({ notaFiscalId, embarcadorId, transportadoraId }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [itens, setItens] = useState([]);

  useEffect(() => {
    if (!notaFiscalId) return undefined;
    let active = true;
    setLoading(true);
    setError("");
    api
      .get(`/pesquisa/notas-fiscais/${notaFiscalId}/ocorrencias`, {
        params: { embarcadorId: embarcadorId || undefined, transportadoraId: transportadoraId || undefined },
      })
      .then((response) => {
        if (active) setItens((response.data && response.data.items) || []);
      })
      .catch((requestError) => {
        if (!active) return;
        setError(
          (requestError.response && requestError.response.data && requestError.response.data.error) ||
            "Não foi possível carregar as ocorrências."
        );
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [embarcadorId, notaFiscalId, transportadoraId]);

  if (loading) return <div className="nfe-detail-state"><Spinner color="info" /> Carregando ocorrências...</div>;
  if (error) return <div className="nfe-detail-state nfe-detail-state--error">{String(error)}</div>;

  return (
    <div className="nfe-detail-links nfe-detail-ocorrencias" data-testid="nf-ocorrencias">
      <Section eyebrow="Rastreio" title="Ocorrências da NFe">
        {!itens.length ? (
          <div className="nfe-detail-empty">Nenhuma ocorrência registrada para esta NFe.</div>
        ) : (
          <div className="nfe-detail-table-wrap">
            <table className="nfe-detail-table hub-thead-accent">
              <thead>
                <tr>
                  <th>Data</th>
                  <th>Ocorrência</th>
                  <th>Descrição</th>
                  <th>Finalizadora</th>
                </tr>
              </thead>
              <tbody>
                {itens.map((o) => (
                  <tr key={o.id}>
                    <td className="nfe-detail-mono">{fmtDate(o.data, true)}</td>
                    <td>
                      <strong>{o.nome || "-"}</strong>
                      {o.codigo ? <small>Código {o.codigo}</small> : null}
                    </td>
                    <td>{o.descricao || o.observacao || "-"}</td>
                    <td>
                      <span className={`nfe-detail-pill nfe-detail-pill--${o.finalizadora ? "success" : "neutral"}`}>
                        {o.finalizadora ? "Sim" : "Não"}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Section>
    </div>
  );
};

const NotaFiscalDetailModal = ({
  isOpen,
  notaFiscalId,
  embarcadorId,
  transportadoraId,
  onClose,
  mostrarOcorrencias = false,
}) => {
  const [activeTab, setActiveTab] = useState("overview");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [data, setData] = useState(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!isOpen || !notaFiscalId) return undefined;
    let active = true;
    setLoading(true);
    setError("");
    setData(null);
    setActiveTab("overview");
    api
      .get(`/notas-fiscais/consulta/${notaFiscalId}`, {
        params: {
          embarcadorId: embarcadorId || undefined,
          transportadoraId: transportadoraId || undefined,
        },
      })
      .then((response) => {
        if (active) setData(response.data);
      })
      .catch((requestError) => {
        if (!active) return;
        setError(
          (requestError.response && requestError.response.data && requestError.response.data.error) ||
            "Não foi possível carregar o detalhe da NFe."
        );
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [embarcadorId, isOpen, notaFiscalId, transportadoraId]);

  const summary = data && data.summary;
  const status = useMemo(() => statusMeta(summary), [summary]);

  const copyKey = () => {
    if (!summary || !summary.chaveAcesso || !navigator.clipboard) return;
    navigator.clipboard.writeText(summary.chaveAcesso).then(() => {
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1600);
    });
  };

  return (
    <Modal
      isOpen={isOpen}
      toggle={onClose}
      size="xl"
      modalClassName="modal-align-top nfe-detail-modal-wrap"
      contentClassName="nfe-detail-modal"
      fade={false}
    >
      <div className="nfe-detail-header">
        <div>
          <span>Detalhe da NFe</span>
          <h3>{summary ? `${summary.numero} / ${summary.serie}` : "Carregando documento"}</h3>
        </div>
        <div className="nfe-detail-header__actions">
          {summary && <span className={`nfe-detail-pill nfe-detail-pill--${status.tone}`}>{status.label}</span>}
          <button type="button" onClick={onClose} title="Fechar" aria-label="Fechar">
            ×
          </button>
        </div>
      </div>

      {summary && (
        <div className="nfe-detail-keybar">
          <div>
            <span>Chave de acesso</span>
            <strong className="nfe-detail-mono">{summary.chaveAcesso || "-"}</strong>
          </div>
          <Button type="button" color="default" onClick={copyKey} disabled={!summary.chaveAcesso}>
            <i className="tim-icons icon-single-copy-04" /> {copied ? "Copiada" : "Copiar"}
          </Button>
        </div>
      )}

      <div className="nfe-detail-body">
        {loading && <div className="nfe-detail-state"><Spinner color="info" /> Carregando detalhe...</div>}
        {!loading && error && <div className="nfe-detail-state nfe-detail-state--error">{String(error)}</div>}
        {!loading && !error && data && summary && (
          <>
            <div className="nfe-detail-tabs" role="tablist" aria-label="Seções do detalhe da NFe">
              {[
                ["overview", "Visão geral"],
                ["calculation", "Cálculo do frete"],
                ["items", `Itens da NFE (${(data.itens || []).length})`],
                ["links", `Vínculos (${(data.ctes || []).length})`],
                // PO1-3378 (épico PO1-2570): envio e etiqueta via conector a partir da NF.
                ["etiqueta", "Etiqueta"],
              ]
                // PO1-2967: aba Ocorrências na Consulta NFe e, desde a PO1-3842, na nota
                // aberta pelas Notas vinculadas da Consulta CTE.
                .concat(mostrarOcorrencias ? [["ocorrencias", "Ocorrências"]] : [])
                .map(([id, label]) => (
                <button
                  key={id}
                  type="button"
                  className={activeTab === id ? "is-active" : ""}
                  onClick={() => setActiveTab(id)}
                  role="tab"
                  aria-selected={activeTab === id}
                >
                  {label}
                </button>
              ))}
            </div>
            {activeTab === "overview" && <OverviewTab summary={summary} />}
            {activeTab === "calculation" && (
              <CalculationTab
                summary={summary}
                composicao={data.composicao || {}}
                composicaoRedespacho={data.composicaoRedespacho}
                ctes={data.ctes || []}
              />
            )}
            {activeTab === "items" && <ItemsTab itens={data.itens || []} />}
            {activeTab === "links" && <LinksTab summary={summary} ctes={data.ctes || []} />}
            {activeTab === "etiqueta" && (
              <EtiquetaTab notaFiscalId={notaFiscalId} embarcadorId={embarcadorId} />
            )}
            {activeTab === "ocorrencias" && mostrarOcorrencias && (
              <OcorrenciasTab notaFiscalId={notaFiscalId} embarcadorId={embarcadorId} transportadoraId={transportadoraId} />
            )}
          </>
        )}
      </div>
    </Modal>
  );
};

export default NotaFiscalDetailModal;
